import Vue from 'vue'
// The prebuilt single-file bundle: the package's own build pulls in dependencies written in
// syntax this webpack 2 build can't parse.
import io from 'socket.io-client/dist/socket.io.js'
import api from './api'
import { getAccessToken, isLoggedIn } from './auth'

// The notification center's client state, shared by the bell, toasts, banners and the
// notifications page. The server's /notifications endpoints are the record; the WebSocket
// (server/src/notifications/realtime.js) only says that something new arrived, so the list is
// reloaded whenever the connection (re)opens and nothing depends on a push getting through.
//
// No object spread in this folder: client/utils/ isn't run through Babel.

let host = window.location.hostname
host += host === 'localhost' ? ':8080' : ''
const serverUrl = `${window.location.protocol}//${host}`

const MAX_ITEMS = 50

export const notificationCenter = new Vue({
  data: {
    status: 'idle', // idle | connecting | connected | reconnecting | error
    items: [],
    unread: 0,
    loading: false,
    loaded: false
  }
})

let socket = null

function upsert (item) {
  const items = notificationCenter.items.filter(n => n.id !== item.id)
  items.unshift(item)
  items.sort((a, b) => b.id - a.id)
  notificationCenter.items = items.slice(0, MAX_ITEMS)
}

export function loadNotifications () {
  notificationCenter.loading = true
  return api.buildRequest('notifications', 'get', { limit: 20 })
    .then(({ data, status }) => {
      if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
      notificationCenter.items = data.payload.items
      notificationCenter.unread = data.payload.unread
      notificationCenter.loaded = true
    })
    .catch(err => console.warn('[notifications] load failed:', err.message))
    .then(() => { notificationCenter.loading = false })
}

export function markNotificationRead (item) {
  if (!item || item.is_read) return Promise.resolve()
  item.is_read = true
  notificationCenter.unread = Math.max(0, notificationCenter.unread - 1)
  return api.buildRequest(`notifications/${item.id}/read`, 'post', {}).then(({ data, status }) => {
    if (status === 200 && data.payload) notificationCenter.unread = data.payload.unread
  })
}

export function markAllNotificationsRead () {
  notificationCenter.items.forEach(n => { n.is_read = true })
  notificationCenter.unread = 0
  return api.buildRequest('notifications/read-all', 'post', {})
}

export function connectNotifications () {
  if (socket || !isLoggedIn() || !getAccessToken()) return
  notificationCenter.status = 'connecting'
  loadNotifications()
  socket = io(serverUrl, {
    path: '/api/socket.io',
    // A function, so a reconnect sends the current token, not the one from page load.
    auth: cb => cb({ token: getAccessToken() }),
    // WebSocket only: Heroku routes each request to a random dyno, which breaks long-polling
    // without sticky sessions. A network that blocks WebSockets still gets the list on load.
    transports: ['websocket']
  })
  socket.on('connect', () => {
    // Anything that arrived while disconnected is picked up here.
    if (notificationCenter.status === 'reconnecting') loadNotifications()
    notificationCenter.status = 'connected'
  })
  socket.on('disconnect', () => { notificationCenter.status = 'reconnecting' })
  socket.on('connect_error', () => { notificationCenter.status = 'error' })
  socket.io.on('reconnect_attempt', () => { notificationCenter.status = 'reconnecting' })
  socket.on('notification', payload => {
    const known = notificationCenter.items.some(n => n.id === payload.id)
    const item = Object.assign({}, payload, { is_read: false })
    upsert(item)
    if (!known) {
      notificationCenter.unread += 1
      notificationCenter.$emit('notification', item)
    }
  })
}

export function disconnectNotifications () {
  if (!socket) return
  socket.disconnect()
  socket = null
  notificationCenter.status = 'idle'
}

export const SEVERITY_ICONS = {
  critical: 'fa-exclamation-circle',
  warning: 'fa-exclamation-triangle',
  success: 'fa-check-circle',
  info: 'fa-info-circle'
}

// "just now", "5 min ago", "3 h ago", then the local date.
export function timeAgo (value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const minutes = Math.round((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} h ago`
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}



//////////////////
// WEBPACK FOOTER
// ./utils/notifications.js
// module id = PxJ7
// module chunks = 1